import * as React from 'react';
import {
  calculateProgress,
  PrimaryActionButtonProps,
  primaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {RegistrationData} from '../data/types';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.registerPassword;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
  };
  userAccount: RegistrationData['account'];
  passwordInputProps: {
    value: string | null;
    onChange: (value: string) => unknown;
    onValidationError: (error: unknown) => void;
    error: string | undefined;
  };
  rememberMeCheckboxProps: {
    value: boolean;
    onChange: (checked: boolean) => unknown;
  };
};

export type RegisterPasswordScreenType = React.ComponentType<ScreenProps>;

export function RegisterPassword(
  props: Props & {children: RegisterPasswordScreenType}
) {
  const [
    {
      context: {
        account,
        validationErrors,
        rememberMe,
        navigator: {location},
      },
      matches,
    },
    send,
  ] = props.registrationMachine;

  const progress = calculateProgress({
    draftAccount: account,
    location,
  });

  const actionButtons = {
    primary: primaryActionButtonProps({
      children: lang.registerNames.buttons.primary.title,
      loading: matches({
        [registrationMachine.RegistrationState.password]:
          registrationMachine.RegistrationState.submittingPassword,
      }),
      disabled: Boolean(
        !account.password || validationErrors?.account?.password
      ),
    }),
  };

  const validate = React.useCallback(
    (error: unknown) => {
      send({
        type: registrationMachine.RegistrationEvent.HAS_VALIDATION_ERRORS,
        errors: {
          account: {password: error as string | undefined},
        },
      });
    },
    [send]
  );

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.registerPassword,
    onFormSubmit: e => {
      e.preventDefault();
      send({
        type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
        payload: {password: account.password},
      });
    },
    progress,
    actionButtons,
    userAccount: account,
    passwordInputProps: {
      value: account.password,
      onChange: password => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {
            account: {password},
          },
        });
      },
      onValidationError: validate,
      error: validationErrors?.account?.password,
    },
    rememberMeCheckboxProps: {
      value: rememberMe,
      onChange: rememberMe => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {rememberMe},
        });
      },
    },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
}
